import { create } from 'zustand';
import {
  DEFAULT_CONFIG,
  type ChatEntry,
  type DrawCommand,
  type GameConfig,
  type PlayerId,
  type PrivateState,
  type RoomSnapshot,
} from '../../../shared/types';

export type Phase = 'lobby' | 'playing' | 'gameover';

interface GameStore {
  connected: boolean;
  error: string | null;
  notice: string | null;
  roomCode: string | null;
  myPlayerId: PlayerId | null;
  identity: string | null;
  myName: string | null;
  phase: Phase;
  snapshot: RoomSnapshot | null;
  privateState: PrivateState | null;
  chat: ChatEntry[];
  config: GameConfig;
  hint: string;

  // Actions — only useGameSocket / callbacks may write to the store.
  setConnected: (connected: boolean) => void;
  setError: (error: string | null) => void;
  setNotice: (notice: string | null) => void;
  setConnectionInfo: (info: { roomCode: string; playerId: PlayerId; identity: string }) => void;
  setSnapshot: (snapshot: RoomSnapshot) => void;
  setPrivateState: (state: PrivateState) => void;
  setHint: (hint: string) => void;
  pushChat: (entry: ChatEntry) => void;
  appendDraw: (commands: DrawCommand[]) => void;
  setConfig: (config: GameConfig) => void;
  reset: () => void;
}

const initialSnapshot: RoomSnapshot = {
  roomCode: '',
  hostId: '',
  phase: 'lobby',
  players: [],
  config: { ...DEFAULT_CONFIG },
  currentDrawerId: null,
  wordLength: null,
  hint: '',
  totalRounds: null,
  roundIndex: 0,
  turnDeadline: null,
  winnerId: null,
  chat: [],
  drawCommands: [],
};

// ── Per-tab session persistence ──────────────────────────────────────────────
// sessionStorage survives page refreshes but is scoped to THIS tab, so a
// refresh resumes the game here while other tabs stay independent.
const SESSION_KEY = 'scribble-session';

interface StoredSession {
  roomCode: string;
  playerId: PlayerId;
  identity: string;
}

function loadSession(): StoredSession | null {
  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Partial<StoredSession>;
    if (
      typeof s.roomCode === 'string' &&
      typeof s.playerId === 'string' &&
      typeof s.identity === 'string'
    ) {
      return { roomCode: s.roomCode, playerId: s.playerId, identity: s.identity };
    }
  } catch {
    /* corrupted storage — ignore */
  }
  return null;
}

function saveSession(session: StoredSession | null): void {
  try {
    if (session) {
      window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
    } else {
      window.sessionStorage.removeItem(SESSION_KEY);
    }
  } catch {
    /* storage unavailable — refresh-resume just won't work */
  }
}

const storedSession = loadSession();

const initial = {
  connected: false,
  error: null as string | null,
  notice: null as string | null,
  roomCode: storedSession?.roomCode ?? null,
  myPlayerId: storedSession?.playerId ?? null,
  identity: storedSession?.identity ?? null,
  myName: null as string | null,
  phase: 'lobby' as Phase,
  snapshot: null as RoomSnapshot | null,
  privateState: null as PrivateState | null,
  chat: [] as ChatEntry[],
  config: { ...DEFAULT_CONFIG },
  hint: '',
};

export const useGameStore = create<GameStore>((set) => ({
  ...initial,

  setConnected: (connected) => set({ connected }),
  setError: (error) => set({ error }),
  setNotice: (notice) => set({ notice }),
  setConnectionInfo: ({ roomCode, playerId, identity }) => {
    saveSession({ roomCode, playerId, identity });
    set({ roomCode, myPlayerId: playerId, identity });
  },
  setSnapshot: (snapshot) =>
    set({
      snapshot,
      phase: snapshot.phase,
      chat: snapshot.chat,
      config: snapshot.config,
      hint: snapshot.hint,
    }),
  setPrivateState: (state) => set({ privateState: state }),
  setHint: (hint) => set({ hint }),
  pushChat: (entry) =>
    set((s) => {
      const chat = [...s.chat, entry];
      if (chat.length > 200) chat.shift();
      return { chat };
    }),
  appendDraw: (commands) =>
    set((s) => {
      if (!s.snapshot) return {};
      return {
        snapshot: {
          ...s.snapshot,
          drawCommands: [...(s.snapshot.drawCommands ?? []), ...commands],
        },
      };
    }),
  setConfig: (config) => set({ config }),
  reset: () => {
    saveSession(null);
    set({ ...initial, roomCode: null, myPlayerId: null, identity: null });
  },
}));

/** Convenience selectors used across components. */
export const useMyId = () => useGameStore((s) => s.myPlayerId);
export const useRoomCode = () => useGameStore((s) => s.roomCode);
export const usePhase = () => useGameStore((s) => s.phase);
export const useConfig = () => useGameStore((s) => s.snapshot?.config ?? DEFAULT_CONFIG);
export const useSnapshot = () => useGameStore((s) => s.snapshot);
export const useDrawCommands = () => useGameStore((s) => s.snapshot?.drawCommands ?? []);
export const usePrivateState = () => useGameStore((s) => s.privateState);
export const useHint = () => useGameStore((s) => s.hint);

/** Are YOU the current drawer, this round? */
export const useAmDrawer = () =>
  useGameStore((s) =>
    s.phase === 'playing' &&
    s.snapshot?.currentDrawerId != null &&
    s.snapshot.currentDrawerId === s.myPlayerId,
  );

/** True when you are a guesser who hasn't nailed the word yet. */
export const useCanGuess = () =>
  useGameStore((s) => {
    if (s.phase !== 'playing' || !s.snapshot) return false;
    if (s.snapshot.currentDrawerId === s.myPlayerId) return false;
    const me = s.snapshot.players.find((p) => p.id === s.myPlayerId);
    return me ? !me.guessed : false;
  });